import classNames from 'classnames';

type PanelProps = {
  children: React.ReactNode;
  className?: string;
};

export const Panel = ({ children, className }: PanelProps) => (
  <section
    className={classNames(
      // min-w-0 keeps wide content (the scrollable charts) overflowing inside
      // the panel; as a grid child the implicit min-width would otherwise
      // stretch the track and put a horizontal scrollbar on the whole page.
      'min-w-0 rounded-[14px] border border-neutral-200 bg-white',
      className
    )}
  >
    {children}
  </section>
);

type PanelHeaderProps = {
  title: string;
  subtitle?: string;
  /** Rendered right-aligned: a legend, a status chip, or a link out. */
  action?: React.ReactNode;
};

export const PanelHeader = ({ title, subtitle, action }: PanelHeaderProps) => (
  <div className="flex flex-wrap items-start justify-between gap-3 px-5 pt-5 pb-4 sm:px-6">
    <div className="min-w-0">
      <h2 className="font-sans text-[17px] font-bold leading-tight text-neutral-800">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-1 text-[13px] text-neutral-500">{subtitle}</p>
      )}
    </div>
    {/* max-w-full keeps a wide action (the form chart's legend and span
        control) wrapping within the panel on narrow viewports instead of
        running off the right edge. */}
    {action && <div className="max-w-full shrink-0">{action}</div>}
  </div>
);

type StatLabelProps = {
  children: React.ReactNode;
  className?: string;
};

export const StatLabel = ({ children, className }: StatLabelProps) => (
  <p
    className={classNames(
      'text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-500',
      className
    )}
  >
    {children}
  </p>
);

export type ChipTone = 'neutral' | 'success' | 'warning' | 'danger' | 'primary';

const CHIP_TONE_CLASS: Record<ChipTone, string> = {
  neutral: 'bg-neutral-100 text-neutral-700',
  success: 'bg-success-50 text-success-700',
  warning: 'bg-warning-50 text-warning-700',
  danger: 'bg-danger-50 text-danger-700',
  primary: 'bg-primary-50 text-primary-700'
};

type ChipProps = {
  children: React.ReactNode;
  tone?: ChipTone;
};

export const Chip = ({ children, tone = 'neutral' }: ChipProps) => (
  <span
    className={classNames(
      'inline-flex items-center rounded-md px-2 py-1 text-xs font-bold',
      CHIP_TONE_CLASS[tone]
    )}
  >
    {children}
  </span>
);

type EmptyModuleProps = {
  title: string;
  message: string;
};

/**
 * Stands in for a module whose data does not exist for the current filters —
 * a pre-2025 season with no Opta counts, or a player with no minutes. Showing
 * this beats rendering a panel full of zeroes, which reads as real data.
 */
export const EmptyModule = ({ title, message }: EmptyModuleProps) => (
  <Panel>
    <PanelHeader title={title} />
    <div className="border-t border-neutral-200 px-5 py-8 text-center sm:px-6">
      <p className="mx-auto max-w-md text-sm text-neutral-500">{message}</p>
    </div>
  </Panel>
);
